// client/src/components/pages/LexiStar/Dwell/Dwell.tsx
//
// LexiStar dwell analytics (ST-00188 M4-D, T7-S04; LS-03 FR-RPT-001 "average
// cycle and dwell times per site", FR-RPT-006; m4-contracts §3, §4, §8).
// Route /lexistar/dwell, page permission lexistar.kpis.view. Reads
// ?from&to&site — the scheme of the metric pack's dwell drill links.
//
//  - Per site, the dwell distribution for each context (loading / offloading
//    / waiting / weighbridge): visits, median, 90th percentile, visits over
//    the limit, and the histogram against the limit.
//  - Exceedance days (median past the limit; each raised an
//    ANALYTICS.DWELL_EXCEEDED event) with a way into the trips of that day.
//  - The limits editor (create / edit; the contract has no delete).
// All figures are the API's; the page does not compute a dwell.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaHourglassHalf } from 'react-icons/fa';
import { createDwellLimit, getDwell, listDwellLimits, updateDwellLimit } from '../../../../services/lexistarAnalytics';
import { listSites } from '../../../../services/lexistarRoutes';
import type { DwellLimit, DwellLimitInput, DwellLimitsResponse, DwellResponse } from '../../../../types/lexistar-analytics';
import { NoPermissionPage } from '../StatusBoard/atoms';
import { PageHeader, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { PeriodPicker, SkeletonBlock, cardClasses } from '../Dashboard/analyticsAtoms';
import { dayLabel, periodLabel, readPeriod } from '../Dashboard/periodLogic';
import DwellHistogram from './DwellHistogram';
import DwellLimits, { type SiteOption } from './DwellLimits';
import { alertsOffNote, axisMax, contextLabel, CONTEXTS, fmtMin, siteViews, sortedExceedances } from './dwellLogic';

/** The dwell-limits routes' write permission (M4-A: POST / PATCH /dwell-limits). */
export const LIMITS_MANAGE_KEYS = ['lexistar.trips.manage'];

type ErrorLike = { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
const errorText = (err: ErrorLike, fallback: string): string =>
  err?.response?.status === 403
    ? 'You do not have permission to read dwell figures.'
    : err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

export default function Dwell() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.kpis.view');
  const canManage = LIMITS_MANAGE_KEYS.some((k) => hasPermission(k));
  const [params, setParams] = useSearchParams();
  const period = readPeriod(params);
  const site = params.get('site') || '';
  const periodKey = `${period.from}|${period.to}`;

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const [data, setData] = useState<DwellResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<DwellLimit[]>([]);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    getDwell({ site: site || undefined, from: period.from, to: period.to })
      .then((res: DwellResponse) => alive && setData(res))
      .catch((err: ErrorLike) => {
        if (!alive) return;
        setData(null);
        setError(errorText(err, 'Dwell could not be loaded.'));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // periodKey carries the period.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, periodKey, site, reload]);

  const loadLimits = useCallback(
    () =>
      listDwellLimits()
        .then((res: DwellLimitsResponse) => setLimits(res?.limits || []))
        .catch(() => setLimits([]))
        .finally(() => setLimitsLoading(false)),
    []
  );
  useEffect(() => {
    if (!canView) return;
    loadLimits();
    listSites()
      .then((rows: unknown) =>
        setSites(
          (Array.isArray(rows) ? (rows as SiteOption[]) : [])
            .filter((s) => s && s.location_id !== undefined)
            .sort((a, b) => String(a.location_name).localeCompare(String(b.location_name)))
        )
      )
      .catch(() => setSites([]));
  }, [canView, loadLimits]);

  const views = useMemo(() => siteViews(data), [data]);
  const exceedances = useMemo(() => sortedExceedances(data?.exceedances), [data]);
  const siteChoices = useMemo(() => {
    const byId = new Map<number, string>();
    sites.forEach((s) => byId.set(s.location_id, s.location_name));
    (data?.sites || []).forEach((s) => s.name && byId.set(s.location_id, s.name));
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [sites, data]);

  const onCreate = async (body: DwellLimitInput) => {
    await createDwellLimit(body);
    await loadLimits();
    setReload((n) => n + 1);
  };
  const onUpdate = async (l: DwellLimit, body: DwellLimitInput) => {
    try {
      await updateDwellLimit(l.id, { max_min: body.max_min, min_loading_min: body.min_loading_min ?? null, version: l.version });
    } finally {
      await loadLimits();
    }
    setReload((n) => n + 1);
  };

  const header = (
    <PageHeader
      icon={FaHourglassHalf}
      title="Dwell at sites"
      subtitle="How long trucks spend loading, offloading, waiting and at the weighbridge — against each site's limits."
      toolbar={
        canView ? (
          <>
            <PeriodPicker period={period} onChange={(p) => update({ from: p.from, to: p.to })} />
            <div className="w-full sm:w-60">
              <ThemedSelect
                aria-label="Site"
                value={site}
                isClearable
                onChange={(e) => update({ site: e.target.value || null })}
                className="text-sm"
              >
                <option value="">All sites</option>
                {siteChoices.map(([id, name]) => (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </>
        ) : null
      }
      inline={false}
    />
  );
  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view dwell analytics." />;

  const totalVisits = views.reduce((a, s) => a + s.visits, 0);
  const tripsOf = (day: string, locationId: number) =>
    `/lexistar/trips?${new URLSearchParams({ from: day, to: day, site_id: String(locationId) })}`;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <p className="text-xs text-slate-600 tabular-nums" role="status" aria-live="polite">
          {loading
            ? 'Loading dwell…'
            : `${periodLabel(period)} · ${views.length} site${views.length === 1 ? '' : 's'} · ${totalVisits.toLocaleString('en-GB')} visits · ${exceedances.length} exceedance day${exceedances.length === 1 ? '' : 's'}`}
        </p>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
          <section className="xl:col-span-8 space-y-3 min-w-0" aria-label="Dwell by site">
            {loading && !data ? (
              Array.from({ length: 3 }, (_, i) => <SkeletonBlock key={i} className="h-40 rounded-xl" />)
            ) : !views.length ? (
              <div className={`${cardClasses} px-4 py-10 text-center`}>
                <p className="text-sm font-medium text-slate-900">No site visits in this period</p>
                <p className="text-xs text-slate-600 mt-1">Dwell is measured from geofence visits at source, destination, weighbridge and delay zones.</p>
              </div>
            ) : (
              views.map((s) => (
                <article key={s.location_id} className={`${cardClasses} p-4`} aria-labelledby={`site-${s.location_id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                    <h2 id={`site-${s.location_id}`} className="text-sm font-semibold text-slate-900">
                      {s.name}
                    </h2>
                    <p className="text-xs text-slate-500 tabular-nums">
                      {s.visits.toLocaleString('en-GB')} visit{s.visits === 1 ? '' : 's'}
                      {s.over > 0 && (
                        <span className="ml-2 font-semibold text-slate-800">
                          <span aria-hidden="true">⏳ </span>
                          {s.over} over limit
                        </span>
                      )}
                    </p>
                  </div>
                  {alertsOffNote(s.alertsOffZones) && (
                    <p className="mb-2 text-xs text-slate-600" data-testid={`dwell-alerts-off-${s.location_id}`}>
                      {alertsOffNote(s.alertsOffZones)}
                    </p>
                  )}
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full min-w-[640px] table-fixed text-sm" aria-label={`Dwell at ${s.name}`}>
                      <colgroup>
                        <col className="w-28" />
                        <col className="w-16" />
                        <col className="w-20" />
                        <col className="w-20" />
                        <col className="w-20" />
                        <col className="w-14" />
                        <col />
                      </colgroup>
                      <thead>
                        <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                          <th scope="col" className="px-1 py-1.5">
                            Context
                          </th>
                          <th scope="col" className="px-1 py-1.5 text-right">
                            Visits
                          </th>
                          <th scope="col" className="px-1 py-1.5 text-right">
                            Median
                          </th>
                          <th scope="col" className="px-1 py-1.5 text-right">
                            90%
                          </th>
                          <th scope="col" className="px-1 py-1.5 text-right">
                            Limit
                          </th>
                          <th scope="col" className="px-1 py-1.5 text-right">
                            Over
                          </th>
                          <th scope="col" className="px-2 py-1.5">
                            Distribution
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {s.contexts.map((d) => (
                          <tr key={d.context} className="tabular-nums">
                            <th scope="row" className="px-1 py-1.5 text-left font-medium text-slate-800" title={CONTEXTS.find((c) => c.id === d.context)?.help}>
                              {contextLabel(d.context)}
                            </th>
                            <td className="px-1 py-1.5 text-right text-slate-700">{d.visits.toLocaleString('en-GB')}</td>
                            <td className="px-1 py-1.5 text-right font-semibold text-slate-900">{fmtMin(d.p50_min)}</td>
                            <td className="px-1 py-1.5 text-right text-slate-700">{fmtMin(d.p90_min)}</td>
                            <td className="px-1 py-1.5 text-right text-slate-700">{d.limit_min !== null ? fmtMin(d.limit_min) : <span className="text-slate-400">none</span>}</td>
                            <td className="px-1 py-1.5 text-right text-slate-700">{d.limit_min !== null ? d.over_limit_visits : '—'}</td>
                            <td className="px-2 py-1">
                              <DwellHistogram d={d} max={axisMax(d)} label={`${contextLabel(d.context)} at ${s.name}`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </article>
              ))
            )}
            <p className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-2.5 rounded-sm bg-slate-400" /> Visits by dwell time
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-2.5 rounded-sm bg-orange-500" /> Past the limit
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-4 h-0 border-t border-dashed border-slate-700" /> Limit
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-0.5 h-2.5 bg-slate-900" /> Median
              </span>
              {data?.method && <span>{data.method}</span>}
            </p>
          </section>

          <div className="xl:col-span-4 space-y-4 min-w-0">
            <section className={`${cardClasses} p-4`} aria-labelledby="exceedances-title">
              <h2 id="exceedances-title" className="text-sm font-semibold text-slate-900 mb-1">
                Exceedance days
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Days a site's median dwell ran past its limit. A day alerts when the visits to zones with alerts on ran past it by themselves.
              </p>
              {loading && !data ? (
                <SkeletonBlock className="h-24" />
              ) : !exceedances.length ? (
                <p className="text-sm text-slate-600 py-2">None in this period.</p>
              ) : (
                <ul className="divide-y divide-slate-100 -mx-1" aria-label="Exceedance days">
                  {exceedances.slice(0, 30).map((x) => (
                    <li key={`${x.day}-${x.location_id}-${x.context}`}>
                      <Link
                        to={tripsOf(x.day, x.location_id)}
                        className="flex items-start justify-between gap-3 rounded-md px-1 py-2 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-slate-900 truncate">
                            {x.site_name || `Site ${x.location_id}`} · {contextLabel(x.context)}
                          </span>
                          <span className="block text-xs text-slate-500 tabular-nums">
                            {dayLabel(x.day)} · {x.visits} visit{x.visits === 1 ? '' : 's'}
                          </span>
                        </span>
                        <span className="text-right text-xs tabular-nums flex-shrink-0">
                          <span className="block font-semibold text-slate-900">{fmtMin(x.p50_min)}</span>
                          {x.on_p50_min !== undefined && (
                            <span className="block text-slate-700" data-testid={`dwell-on-median-${x.day}-${x.location_id}-${x.context}`}>
                              alerts on {fmtMin(x.on_p50_min)}
                            </span>
                          )}
                          <span className="block text-slate-500">limit {fmtMin(x.limit_min)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <DwellLimits
              limits={limits}
              sites={sites}
              canManage={canManage}
              onCreate={onCreate}
              onUpdate={onUpdate}
              loading={limitsLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
